import { Alert, Button, Input, Result, Space, Spin, Steps, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import ReportEditor from "../../core/components/ReportEditor";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";

// API rapor içeriğini camelCase döner; ReportEditor bölümleri PascalCase okur.
const toEditorContent = (content) => ({
    Teknik: content?.teknik ?? content?.Teknik ?? '',
    Bulgular: content?.bulgular ?? content?.Bulgular ?? '',
    Karsilastirma: content?.karsilastirma ?? content?.Karsilastirma ?? '',
    SonucVeOneriler: content?.sonucVeOneriler ?? content?.SonucVeOneriler ?? '',
})

/**
 * "AI Rapor (Test)": sesten rapor hattını herhangi bir istemde (onaylı raporlu olanlar dahil) denemek ve Modal'a
 * geri bildirim göndermek için. Rapor RIS'e KAYDEDİLMEZ; Weasis akışından ve rapor ekranından bağımsızdır.
 *
 * Akış: kayıtlı son transcript (yoksa üretilir; "Yeniden oluştur" yeniler) → onay → rapor üretimi → düzenleme →
 * onay = geri bildirim (report fix; transcript düzenlendiyse transcript fix).
 */
export default function AiReportTestModal({ studyId, requestId }) {
    const { t } = useTranslation()
    const { hasPermission } = useUser()
    const canEditTranscript = hasPermission(PERMISSIONS.AI_DICTATION_EDIT_TRANSCRIPT)

    const [step, setStep] = useState(0)
    const [busy, setBusy] = useState(null)
    const [dictation, setDictation] = useState(null)
    const [transcript, setTranscript] = useState('')
    const [generated, setGenerated] = useState(null)
    const [editorKey, setEditorKey] = useState(0)
    const [feedback, setFeedback] = useState(null)
    const report = useRef({})

    const run = async (message, fn) => {
        setBusy(message)
        try {
            await fn()
        }
        finally {
            setBusy(null)
        }
    }

    const transcribe = () => run(t('Converting voice records to text...'), async () => {
        const response = await ApiService.Dictation.Transcribe(studyId)
        if (!response.success)
            return
        setDictation({ id: response.data.id, createdDate: new Date().toISOString(), voiceRecordsChanged: false })
        setTranscript(response.data.transcript || '')
    })

    const loadTranscript = () => run(t('Converting voice records to text...'), async () => {
        const response = await ApiService.AiReportTest.GetTranscript(studyId)
        if (!response.success)
            return
        if (!response.data) {
            await transcribe()
            return
        }
        setDictation(response.data)
        setTranscript(response.data.transcript || '')
    })

    const generate = () => run(t('Generating report...'), async () => {
        const response = await ApiService.Dictation.GenerateReport(studyId, requestId, {
            dictationId: dictation.id,
            transcript
        })
        if (!response.success)
            return
        const content = toEditorContent(response.data.content)
        report.current = content
        setGenerated({ id: response.data.id, content })
        setEditorKey(key => key + 1)
        setFeedback(null)
        setStep(1)
    })

    const sendFeedback = () => run(t('Sending feedback...'), async () => {
        const response = await ApiService.AiReportTest.SendFeedback(generated.id, { content: report.current })
        if (!response.success)
            return
        setFeedback(response.data)
        setStep(2)
    })

    useEffect(() => {
        loadTranscript()
    }, [studyId])

    const fixStatus = (sent, notSentText) => sent ? t('Sent') : notSentText

    return (
        <Spin spinning={!!busy} tip={busy}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Alert type="info" showIcon message={t('This screen is for testing. The report is not saved to RIS.')} />

                <Steps
                    size="small"
                    current={step}
                    items={[
                        { title: t('Transcript') },
                        { title: t('Report') },
                        { title: t('Feedback') },
                    ]}
                />

                {step === 0 && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {dictation?.voiceRecordsChanged && (
                            <Alert
                                type="warning"
                                showIcon
                                message={t('Voice records changed after this transcript was created. You can regenerate it.')}
                            />
                        )}
                        {dictation?.createdDate && (
                            <Typography.Text type="secondary">
                                {t('Transcript created')}: {new Date(dictation.createdDate).toLocaleString()}
                            </Typography.Text>
                        )}
                        <Input.TextArea
                            value={transcript}
                            onChange={e => setTranscript(e.target.value)}
                            readOnly={!canEditTranscript}
                            autoSize={{ minRows: 10, maxRows: 24 }}
                        />
                        <Space>
                            <Button onClick={transcribe} disabled={!!busy}>
                                {t('Regenerate')}
                            </Button>
                            <Button type="primary" onClick={generate} disabled={!!busy || !dictation || !transcript.trim()}>
                                {t('Approve and generate report')}
                            </Button>
                        </Space>
                    </Space>
                )}

                {step === 1 && generated && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <ReportEditor
                            key={editorKey}
                            defaultReportContent={generated.content}
                            onBulgular={(data) => { report.current = { ...report.current, Bulgular: data } }}
                            onSonuc={(data) => { report.current = { ...report.current, SonucVeOneriler: data } }}
                            onKarsilastirma={(data) => { report.current = { ...report.current, Karsilastirma: data } }}
                            onTeknik={(data) => { report.current = { ...report.current, Teknik: data } }}
                        />
                        <Space>
                            <Button onClick={() => setStep(0)} disabled={!!busy}>
                                {t('Back to transcript')}
                            </Button>
                            <Button onClick={generate} disabled={!!busy}>
                                {t('Regenerate report')}
                            </Button>
                            <Button type="primary" onClick={sendFeedback} disabled={!!busy}>
                                {t('Approve and send feedback')}
                            </Button>
                        </Space>
                    </Space>
                )}

                {step === 2 && feedback && (
                    <Result
                        status="success"
                        title={t('Feedback sent')}
                        subTitle={
                            <Space direction="vertical">
                                <span>{t('Report fix')}: {fixStatus(feedback.reportFixSent, t('Not sent: service address is not configured'))}</span>
                                <span>{t('Transcript fix')}: {fixStatus(feedback.transcriptFixSent, feedback.transcriptEdited
                                    ? t('Not sent: service address is not configured')
                                    : t('Not sent: transcript was not edited'))}</span>
                            </Space>
                        }
                        extra={
                            <Button onClick={() => { setGenerated(null); setStep(0) }}>
                                {t('Test again')}
                            </Button>
                        }
                    />
                )}
            </Space>
        </Spin>
    )
}
